// ============================================================================
// CLOSURE REASON HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { closureReasonService } from '../../api/services/closureReasonService';
import type {
  ClosureReason,
  ClosureReasonListItem,
  CreateClosureReasonRequest,
  UpdateClosureReasonRequest,
  ClosureReasonSearchRequest,
} from '../../api/types/closureReason.types';
import type { ID } from '../../api/types';

export const useClosureReasons = (params?: ClosureReasonSearchRequest) => {
  const [data, setData] = useState<ClosureReasonListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchClosureReasons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await closureReasonService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchClosureReasons(); }, [fetchClosureReasons]);

  return { data, loading, error, pagination, refetch: fetchClosureReasons };
};

export const useClosureReason = (id: ID | undefined) => {
  const [data, setData] = useState<ClosureReason | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchClosureReason = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await closureReasonService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchClosureReason(); }, [fetchClosureReason]);

  return { data, loading, error, refetch: fetchClosureReason };
};

export const useCreateClosureReason = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createClosureReason = async (
    data: CreateClosureReasonRequest,
    options?: { onSuccess?: (cr: ClosureReason) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await closureReasonService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createClosureReason, loading, error };
};

export const useUpdateClosureReason = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateClosureReason = async (
    id: ID, data: UpdateClosureReasonRequest,
    options?: { onSuccess?: (cr: ClosureReason) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await closureReasonService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateClosureReason, loading, error };
};

export const useDeleteClosureReason = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteClosureReason = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await closureReasonService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteClosureReason, loading, error };
};
